import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Minus, Pencil, Trash2, Check, X, Newspaper } from "lucide-react";
import { api, errorMessage, formatDate, timeAgo, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";

const kindLabel = { video: "YouTube", tiktok: "TikTok" };

export const ShowEpisodesGroup = ({ show, index, version, onEdit, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [episodes, setEpisodes] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  const load = () => api.get("/admin/episodes", { params: { show_id: show.id, limit: 5000 } }).then((r) => setEpisodes(r.data)).catch(() => setEpisodes([]));
  const toggle = () => { if (!open && episodes === null) load(); setOpen((o) => !o); };
  useEffect(() => { if (open) load(); }, [version]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (ep) => { setEditingId(ep.id); setDraft(ep.title); };
  const saveTitle = async (ep) => {
    const title = draft.trim();
    if (!title || title === ep.title) return setEditingId(null);
    try {
      await api.put(`/admin/episodes/${ep.id}`, { title });
      setEpisodes((list) => list.map((e) => (e.id === ep.id ? { ...e, title } : e)));
      toast.success("Title updated");
    } catch (err) { toast.error(errorMessage(err)); }
    setEditingId(null);
  };
  const remove = async (ep) => {
    if (!window.confirm(`Delete "${ep.title}"?`)) return;
    try { await api.delete(`/admin/episodes/${ep.id}`); setEpisodes((list) => list.filter((e) => e.id !== ep.id)); toast.success("Episode deleted"); onChanged(); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  const writeArticle = async (ep) => {
    if (!window.confirm(`Write an SEO article from "${ep.title}"? It will appear in SEO Articles → Drafts in a few minutes.`)) return;
    try { await api.post(`/admin/articles/from-episode/${ep.id}`); toast.success("Article requested — check SEO Articles → Activity"); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="card-surface rounded-2xl overflow-hidden" data-testid={`show-group-${index}`}>
      <button type="button" onClick={toggle} className="w-full flex items-center gap-4 p-3 text-left hover:bg-white/[0.04] transition-colors" aria-expanded={open} data-testid={`show-group-toggle-${index}`}>
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-slate-200">{open ? <Minus size={16} /> : <Plus size={16} />}</span>
        {show.cover_url ? <img src={imgUrl(show.cover_url)} alt="" className="h-12 w-12 rounded-lg object-cover bg-[#053671]" /> : <div className="h-12 w-12 rounded-lg bg-[#053671]" />}
        <div className="min-w-0 flex-1">
          <p className="font-semibold truncate flex items-center gap-2">{show.name}{kindLabel[show.kind] && <span className="rounded-full bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-rose-400">{kindLabel[show.kind]}</span>}</p>
          <p className="text-xs text-slate-400 truncate" data-testid={`show-group-meta-${index}`}>{show.episode_count} episode{show.episode_count === 1 ? "" : "s"}{show.latest_at ? ` · updated ${timeAgo(show.latest_at)}` : " · no episodes yet"}</p>
        </div>
      </button>
      {open && (
        <div className="border-t border-white/10 divide-y divide-white/5" data-testid={`show-group-episodes-${index}`}>
          {episodes === null && <p className="p-4 text-sm text-slate-400">Loading…</p>}
          {episodes?.length === 0 && <p className="p-4 text-sm text-slate-400">No episodes yet.</p>}
          {episodes?.map((ep, i) => (
            <div key={ep.id} className="flex items-center gap-3 px-3 py-2 pl-14" data-testid={`show-group-${index}-episode-${i}`}>
              <img src={imgUrl(ep.image_url || show.cover_url)} alt="" className="h-9 w-9 rounded-md object-cover bg-[#053671]" />
              <div className="min-w-0 flex-1">
                {editingId === ep.id ? (
                  <form onSubmit={(e) => { e.preventDefault(); saveTitle(ep); }} className="flex items-center gap-2">
                    <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setEditingId(null)} className="h-8 bg-white/5 border-white/10 text-sm" data-testid={`episode-title-inline-input-${index}-${i}`} />
                    <button type="submit" className="p-1.5 text-emerald-400 hover:text-emerald-300" data-testid={`episode-title-inline-save-${index}-${i}`}><Check size={16} /></button>
                    <button type="button" onClick={() => setEditingId(null)} className="p-1.5 text-slate-400 hover:text-white" data-testid={`episode-title-inline-cancel-${index}-${i}`}><X size={16} /></button>
                  </form>
                ) : (
                  <button type="button" onClick={() => startEdit(ep)} title="Click to edit title" className="block w-full text-left text-sm font-medium truncate hover:text-sky-300 transition-colors" data-testid={`episode-title-${index}-${i}`}>{ep.title}</button>
                )}
                <p className="text-xs text-slate-500">{formatDate(ep.published_at)} · <span className={ep.source === "manual" ? "text-amber-400" : "text-sky-400"}>{ep.source}</span></p>
              </div>
              <button onClick={() => onEdit(ep)} className="p-2 text-slate-400 hover:text-white" title="Full edit" data-testid={`edit-episode-${index}-${i}`}><Pencil size={15} /></button>
              {ep.audio_url && <button onClick={() => writeArticle(ep)} className="p-2 text-slate-400 hover:text-sky-300" title="Write an SEO article from this episode" data-testid={`write-article-${index}-${i}`}><Newspaper size={15} /></button>}
              <button onClick={() => remove(ep)} className="p-2 text-slate-400 hover:text-rose-400" data-testid={`delete-episode-${index}-${i}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
